import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { catchError, EMPTY, exhaustMap, filter, from, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs'

import * as ProjectorTemplatesActions from './projector-templates.actions'
import { ProjectorTemplatesApiService } from './projector-templates-api.service'
import {
  ProjectorTemplatesEntity,
  ProjectorTemplateImageEntity,
  ProjectorTemplateRawConfig,
  TEMPLATES_LOADER,
} from './projector-templates.models'
import { GbDashboardDialogService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-dialog-service/gb-dashboard-dialog.service'
import { GbPresenterDialogTemplateConfigComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-dialog-template-config/gb-presenter-dialog-template-config.component'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { PresenterWidgetsFacade } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.facade'
import * as PresenterWidgetsActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import { ProjectorsFacade } from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.facade'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'
import { Store } from '@ngrx/store'
import * as ProjectorTemplatesSelectors from './projector-templates.selectors'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { combineLatest } from 'rxjs'
import { buildProjectorTemplateConfig, ProjectorTemplateConfigBuildInput } from '../projector-template-config.builder'
import { concatLatestFrom } from '@ngrx/operators'
import { ProjectorTemplatesFacade } from './projector-templates.facade'
import { ProjectorsEntity } from '@galabid/gb-dashboard/projector/data-access'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { GbAddProjectorTemplateDialogComponent } from 'libs/gb-dashboard/projector/feature/src/lib/gb-add-projector-template-dialog/gb-add-projector-template-dialog.component'
import { GbNavigationService } from 'libs/shared/utils/services/src/lib/gb-navigation-service/gb-navigation.service'
import { PresenterConstants } from 'libs/gb-dashboard/presenter/feature/src/lib/presenter.constants'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbPresenterDialogTemplatesComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-dialog-templates/gb-presenter-dialog-templates.component'
import * as ProjectorContainersActions from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.actions'

@Injectable()
export class ProjectorTemplatesEffects {
  private actions$ = inject(Actions)
  private store = inject(Store)
  private gbDashboardDialogService = inject(GbDashboardDialogService)
  private projectorTemplatesApiService = inject(ProjectorTemplatesApiService)
  private gbNotificationService = inject(GbNotificationService)
  private presenterWidgetsFacade = inject(PresenterWidgetsFacade)
  private projectorsFacade = inject(ProjectorsFacade)
  private projectorTemplatesFacade = inject(ProjectorTemplatesFacade)
  private gbLoaderService = inject(GbLoaderService)
  private gbNavigationService = inject(GbNavigationService)
  private selectedAuctionFacade = inject(SelectedAuctionFacade)

  private readonly allWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allCountdownWidgets$,
    this.presenterWidgetsFacade.allImageWidgets$,
    this.presenterWidgetsFacade.allTextWidgets$,
    this.presenterWidgetsFacade.allSmartTextWidgets$,
    this.presenterWidgetsFacade.allTotalRaisedWidgets$,
    this.presenterWidgetsFacade.allTeamScoringWidgets$,
    this.presenterWidgetsFacade.allPowerPledgeWidgets$,
    this.presenterWidgetsFacade.allNotificationWidgets$,
    this.presenterWidgetsFacade.allItemTableWidgets$,
    this.presenterWidgetsFacade.allRaffleWinnersWidgets$,
    this.presenterWidgetsFacade.allHonourRollWidgets$,
    this.presenterWidgetsFacade.allThermometerWidgets$,
    this.presenterWidgetsFacade.allRaffleDrawWidgets$,
    this.presenterWidgetsFacade.allQrCodeWidgets$,
  ])

  loadProjectorTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.loadProjectorTemplates),
      switchMap(() =>
        this.projectorTemplatesApiService.getProjectorTemplates().pipe(
          map((templates: ProjectorTemplatesEntity[]) =>
            ProjectorTemplatesActions.loadProjectorTemplatesSuccess({ templates }),
          ),
          catchError((error) => of(ProjectorTemplatesActions.loadProjectorTemplatesFailure({ error }))),
        ),
      ),
    ),
  )

  loadProjectorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.loadProjectorTemplate),
      switchMap(({ uuid }) =>
        this.projectorTemplatesApiService.getProjectorTemplate(uuid).pipe(
          map((template: ProjectorTemplatesEntity) =>
            ProjectorTemplatesActions.loadProjectorTemplateSuccess({ template }),
          ),
          catchError((error) => of(ProjectorTemplatesActions.loadProjectorTemplateFailure({ error }))),
        ),
      ),
    ),
  )

  createProjectorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.createProjectorTemplate),
      switchMap((action) => {
        return this.projectorTemplatesApiService.createProjectorTemplate(action.template).pipe(
          map((template) => ProjectorTemplatesActions.createProjectorTemplateSuccess({ template })),
          catchError((error) => of(ProjectorTemplatesActions.createProjectorTemplateFailure({ error }))),
        )
      }),
    ),
  )

  applySelectedTemplateToSelectedSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.applySelectedTemplateToSelectedSlide),
      concatLatestFrom(() => [
        this.projectorTemplatesFacade.selectedProjectorTemplate$,
        this.projectorsFacade.selectedProjectorId$,
        this.selectedAuctionFacade.urlName$,
      ]),
      switchMap(([, selectedTemplate, selectedProjectorUuid, urlName]) => {
        if (!selectedTemplate?.uuid || !selectedProjectorUuid || !urlName) {
          return EMPTY
        }

        return this.projectorTemplatesApiService
          .loadTemplateIntoProjector({
            templateUuid: selectedTemplate.uuid,
            projectorUuid: selectedProjectorUuid,
            auctionUrlName: urlName,
          })
          .pipe(
            switchMap((projector: ProjectorsEntity) => {
              return of(
                ProjectorActions.updateProjectorSuccess({
                  update: { id: projector.uuid, changes: projector },
                  showToast: false,
                }),
                ProjectorContainersActions.loadProjectorContainerData(),
                ProjectorTemplatesActions.applySelectedTemplateToSelectedSlideSuccess(),
              )
            }),
            catchError((error) => of(ProjectorTemplatesActions.applySelectedTemplateToSelectedSlideFailure({ error }))),
          )
      }),
    ),
  )

  closeTemplatesDialogAfterApply$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorTemplatesActions.applySelectedTemplateToSelectedSlideSuccess),
        tap(() => this.gbDashboardDialogService.closeDialog(GbPresenterDialogTemplatesComponent)),
      ),
    { dispatch: false },
  )

  selectAndCloseDialogAfterAddSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorActions.addSlideSuccess),
      mergeMap((action) => {
        if (action.templateUuid) {
          this.gbDashboardDialogService.closeDialog(GbPresenterDialogTemplatesComponent)
        }
        return [
          ProjectorActions.selectProjector({ uuid: action.projector.uuid }),
          ProjectorContainersActions.loadProjectorContainerData(),
        ]
      }),
    ),
  )

  closeAddProjectorTemplateDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorTemplatesActions.createProjectorTemplateSuccess,
          ProjectorTemplatesActions.createProjectorTemplateFailure,
        ),
        map(() => this.gbDashboardDialogService.closeDialog(GbAddProjectorTemplateDialogComponent, true)),
      ),
    { dispatch: false },
  )

  goToPresenterTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorTemplatesActions.createProjectorTemplateSuccess,
          ProjectorTemplatesActions.goToPresenterTemplate,
        ),
        tap((action) => {
          let uuid
          if (action.type === ProjectorTemplatesActions.createProjectorTemplateSuccess.type) {
            uuid = action.template.uuid
          }
          if (action.type === ProjectorTemplatesActions.goToPresenterTemplate.type) {
            uuid = action.uuid
          }
          const fragments = [`/${PresenterConstants.PRESENTER_TEMPLATE}/${uuid}/`]
          this.gbNavigationService.navigate(fragments, { newTab: true })
        }),
      ),
    { dispatch: false },
  )

  saveTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.saveTemplate),
      switchMap(({ template }) =>
        this.projectorTemplatesApiService.updateProjectorTemplate(template).pipe(
          map((updated) =>
            ProjectorTemplatesActions.updateProjectorTemplateSuccess({
              update: { id: updated.uuid, changes: updated },
            }),
          ),
          catchError((error) => of(ProjectorTemplatesActions.updateProjectorTemplateFailure({ error }))),
        ),
      ),
    ),
  )

  closeProjectorTemplateConfigDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorTemplatesActions.updateProjectorTemplateSuccess),
        tap(() => this.gbDashboardDialogService.closeDialog(GbPresenterDialogTemplateConfigComponent)),
      ),
    { dispatch: false },
  )

  openSaveProjectorAsTemplateDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.openSaveProjectorAsTemplateDialog),
      exhaustMap(({ projectorUuid }) =>
        this.gbDashboardDialogService
          .getDialogObs(GbPresenterDialogTemplateConfigComponent, {
            disableClose: false,
            width: '500px',
            data: {
              templateAsProjector: true,
            },
          })
          .pipe(map((result) => ({ projectorUuid, result }))),
      ),
      filter(({ result }) => !!result),
      map(({ projectorUuid, result }) => {
        const raw = result as {
          name: string
          description: string
          visibility: 'public' | 'client_account'
          clientAccounts?: Array<{ uuid?: string | null }> | null
        }

        const isPublic = raw.visibility === 'public'
        const clientAccountUuids = (raw.clientAccounts ?? [])
          .map((c) => c.uuid)
          .filter((uuid): uuid is string => !!uuid)

        return ProjectorTemplatesActions.saveProjectorAsTemplate({
          projectorUuid,
          name: raw.name,
          description: raw.description,
          is_public_all_client_accounts: isPublic,
          client_accounts: clientAccountUuids,
        })
      }),
    ),
  )

  saveProjectorAsTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.saveProjectorAsTemplate),
      tap(() => this.gbLoaderService.showLoaderDialog(TEMPLATES_LOADER.saveProjectorAsTemplate.id)),
      switchMap(({ projectorUuid, name, description, is_public_all_client_accounts, client_accounts }) =>
        this.projectorTemplatesApiService
          .saveProjectorAsTemplate({
            projector: projectorUuid,
            name,
            schema_version: 2,
            description,
            is_public_all_client_accounts,
            client_accounts,
          })
          .pipe(
            map((template) => ProjectorTemplatesActions.saveProjectorAsTemplateSuccess({ template })),
            catchError((error) => of(ProjectorTemplatesActions.saveProjectorAsTemplateFailure({ error }))),
          ),
      ),
    ),
  )

  saveTemplateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorTemplatesActions.saveProjectorAsTemplateSuccess,
          ProjectorTemplatesActions.updateProjectorTemplateSuccess,
        ),
        tap(() => this.gbNotificationService.success('PRESENTER.TEMPLATE_SAVED')),
      ),
    { dispatch: false },
  )

  goToPresenterTemplateOnCreation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.saveProjectorAsTemplateSuccess),
      map((action) => ProjectorTemplatesActions.goToPresenterTemplate({ uuid: action.template.uuid })),
    ),
  )

  saveProjectorAsTemplateFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorTemplatesActions.saveProjectorAsTemplateFailure),
        tap(() => this.gbNotificationService.error('PRESENTER.TEMPLATE_SAVE_FAILED')),
      ),
    { dispatch: false },
  )

  hideSaveProjectorAsTemplateLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorTemplatesActions.saveProjectorAsTemplateFailure,
          ProjectorTemplatesActions.saveProjectorAsTemplateSuccess,
        ),
        tap(() => {
          this.gbLoaderService.hideLoaderDialog(TEMPLATES_LOADER.saveProjectorAsTemplate.id)
        }),
      ),
    { dispatch: false },
  )

  hideUpdateLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorTemplatesActions.updateProjectorTemplateFailure,
          ProjectorTemplatesActions.updateProjectorTemplateSuccess,
        ),
        tap(() => {
          this.gbLoaderService.hideLoaderDialog(TEMPLATES_LOADER.saveTemplate.id)
        }),
      ),
    { dispatch: false },
  )

  updateProjectorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.updateProjectorTemplate),
      tap(() => this.gbLoaderService.showLoaderDialog(TEMPLATES_LOADER.saveTemplate.id)),
      concatLatestFrom(() => [
        this.allWidgets$,
        this.projectorsFacade.selectedProjectors$,
        this.projectorTemplatesFacade.selectedProjectorTemplate$,
      ]),
      map(([action, widgetArrays, projector, selectedPresenterTemplate]) => {
        const [
          countdownWidgets,
          imageWidgets,
          textWidgets,
          smartTextWidgets,
          totalRaisedWidgets,
          teamScoringWidgets,
          powerPledgeWidgets,
          notificationWidgets,
          itemTableWidgets,
          raffleWinnersWidgets,
          honourRollWidgets,
          thermometerWidgets,
          raffleDrawWidgets,
          qrCodeWidgets,
        ] = widgetArrays

        const selectedProjector = projector as ProjectorsEntity
        const backgroundImage = selectedProjector?.background_image
          ? { uuid: selectedProjector.background_image.uuid }
          : null

        const buildInput: ProjectorTemplateConfigBuildInput = {
          projector: {
            ...selectedProjector,
          },
          countdownWidgets,
          imageWidgets,
          textWidgets,
          smartTextWidgets,
          totalRaisedWidgets,
          teamScoringWidgets,
          powerPledgeWidgets,
          notificationWidgets,
          itemTableWidgets,
          raffleWinnersWidgets,
          honourRollWidgets,
          thermometerWidgets,
          raffleDrawWidgets,
          qrCodeWidgets,
        }

        return ProjectorTemplatesActions.saveTemplate({
          template: {
            uuid: '',
            ...selectedPresenterTemplate,
            ...action.template,
            background_image: backgroundImage,
            config: action.updateConfig ? buildProjectorTemplateConfig(buildInput) : action.template?.config,
          },
        })
      }),
    ),
  )

  // this is for decoding the template from the config field and getting it ready for the user to edit

  initSelectedProjectorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorTemplatesActions.initSelectedProjectorTemplate),
      gbCheckLoaded(this.store.select(ProjectorTemplatesSelectors.selectSelectedProjectorTemplate)),
      withLatestFrom(this.store.select(ProjectorTemplatesSelectors.selectSelectedProjectorTemplate)),
      switchMap(([, template]) => {
        const rawConfig = this.parseProjectorTemplateRawConfig(template)
        if (!rawConfig) {
          return EMPTY
        }

        return from(this.buildInitActionsFromRawConfig(rawConfig, template?.background_image))
      }),
    ),
  )

  private parseProjectorTemplateRawConfig(
    template: ProjectorTemplatesEntity | undefined,
  ): ProjectorTemplateRawConfig | null {
    if (!template?.config) {
      return null
    }

    try {
      return JSON.parse(template.config) as ProjectorTemplateRawConfig
    } catch {
      this.gbNotificationService.error('PRESENTER.TEMPLATE_INVALID_CONFIG')
      return null
    }
  }

  private buildInitActionsFromRawConfig(
    raw: ProjectorTemplateRawConfig,
    backgroundImage?: ProjectorTemplateImageEntity | null,
  ): Action[] {
    const normalize = <T>(widgets: T[] | undefined): T[] =>
      (widgets ?? []).map((w) => ({ ...w, projector: 'template' }))

    const projector = {
      uuid: raw.Projector.uuid ?? 'template',
      name: raw.Projector.name ?? 'template',
      is_active: raw.Projector.is_active ?? false,
      created: '',
      auction: '',
      background_image: backgroundImage
        ? {
            auction: '',
            uuid: backgroundImage.uuid,
            image: backgroundImage.image_url ?? '',
            medium_image: backgroundImage.medium_image ?? '',
            preload_image: backgroundImage.preload_image ?? '',
            small_image: backgroundImage.small_image ?? '',
            thumbnail_image: backgroundImage.thumbnail_image ?? '',
          }
        : null,
      background_image_cover: raw.Projector.background_image_cover ?? true,
      background_color: raw.Projector.background_color ?? '',
      custom_css: raw.Projector.custom_css ?? '',
      container: null,
      font_family: raw.Projector.font_family ?? '',
      color: raw.Projector.color ?? '',
    }

    return [
      ProjectorActions.setSelectedProjectorLocal({ projector }),
      PresenterWidgetsActions.loadTextWidgetsSuccess({ widgets: normalize(raw.TextProjectorWidget) }),
      PresenterWidgetsActions.loadSmartTextWidgetsSuccess({
        widgets: normalize(raw.SmartTextProjectorWidget),
      }),
      PresenterWidgetsActions.loadItemTableWidgetsSuccess({
        widgets: normalize(raw.ItemTableProjectorWidget),
      }),
      PresenterWidgetsActions.loadNotificationWidgetsSuccess({
        widgets: normalize(raw.NotificationProjectorWidget),
      }),
      PresenterWidgetsActions.loadTotalRaisedWidgetsSuccess({
        widgets: normalize(raw.TotalRaisedProjectorWidget),
      }),
      PresenterWidgetsActions.loadRaffleDrawWidgetsSuccess({
        widgets: normalize(raw.RaffleDrawProjectorWidget),
      }),
      PresenterWidgetsActions.loadRaffleWinnersWidgetsSuccess({
        widgets: normalize(raw.RaffleWinnersProjectorWidget),
      }),
      PresenterWidgetsActions.loadCountdownWidgetsSuccess({
        widgets: normalize(raw.CountDownProjectorWidget),
      }),
      PresenterWidgetsActions.loadTeamScoringWidgetsSuccess({
        widgets: normalize(raw.TeamScoringProjectorWidget),
      }),
      PresenterWidgetsActions.loadPowerPledgeWidgetsSuccess({
        widgets: normalize(raw.PowerPledgeProjectorWidget),
      }),
      PresenterWidgetsActions.loadHonourRollWidgetsSuccess({
        widgets: normalize(raw.HonourRollProjectorWidget),
      }),
      PresenterWidgetsActions.loadImageWidgetsSuccess({ widgets: normalize(raw.ImageProjectorWidget) }),
      PresenterWidgetsActions.loadThermometerWidgetsSuccess({
        widgets: normalize(raw.ThermometerProjectorWidget),
      }),
      PresenterWidgetsActions.loadQrCodeWidgetsSuccess({
        widgets: normalize(raw.QrCodeProjectorWidget),
      }),
    ]
  }
}
